import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

import { useToast } from "@/hooks/use-toast";
import Footer from "@/components/Footer";
import { ScaledLivePreview } from "@/components/InstagramMockup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ChevronDown, Download, Trash2, Shield, CreditCard, Send, Instagram } from "lucide-react";
import { getStripeEnvironment } from "@/lib/stripe";
import TeamsSection from "@/components/dashboard/TeamsSection";
import ClubMediaSection from "@/components/dashboard/ClubMediaSection";
import { getClubLogoUrl } from "@/lib/bfv";

interface ClubData {
  id: string;
  club_name: string;
  selected_tier: string;
  liga: string;
  design_template: number;
  selected_design: string;
  primary_color: string;
  secondary_color: string | null;
  telegram_code: string;
  telegram_chat_id: string | null;
  telegram_token: string | null;
  team_name: string | null;
  logo_url: string | null;
  created_at: string;
  youth_pack?: boolean | null;
  instagram_enabled?: boolean | null;
  instagram_connected?: boolean | null;
  sponsor_urls?: string[] | null;
  sponsors_enabled?: boolean | null;
  photo_matchday?: string | null;
  photo_goal?: string | null;
  photo_final?: string | null;
}

const DESIGN_OPTIONS = Array.from({ length: 16 }, (_, i) => {
  const n = String(i + 1);
  return { id: n, name: `Design ${n}` };
});

const RENDER_BASE = (
  (import.meta.env.VITE_RENDER_BASE_URL as string | undefined) || "https://api.torflash.de"
).replace(/\/$/, "");

// Alte Bezeichnungen aus früheren Anmeldungen auf die aktuellen IDs mappen.
const LEGACY_DESIGN_MAP: Record<string, string> = {
  klassisch: "1",
  klassik: "1",
  modern: "2",
  rein: "2",
  stadion: "3",
  bold: "4",
  blitz: "4",
};

const normalizeDesign = (value?: string | null) => {
  if (!value) return "1";
  const mapped = LEGACY_DESIGN_MAP[value.toLowerCase()] ?? value;
  const n = parseInt(mapped, 10);
  return Number.isFinite(n) && n >= 1 && n <= 16 ? String(n) : "1";
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const TELEGRAM_CODE_RE = /^[A-Z0-9]{8}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

export default function DashboardPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [club, setClub] = useState<ClubData | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState("1");
  const [designPickerOpen, setDesignPickerOpen] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#1B5EAB");
  const [code, setCode] = useState<string>("");
  const [creatingCode, setCreatingCode] = useState(false);
  const [checking, setChecking] = useState(false);
  const PORTAL_URL = (import.meta.env.VITE_STRIPE_PORTAL_URL as string | undefined)?.trim() || "";
  const [hasSubscription, setHasSubscription] = useState(false);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState("");
  const { toast } = useToast();

  const handleConnectTelegram = async () => {
    if (!club) return;
    const telegramWindow = window.open("about:blank", "_blank");
    setCreatingCode(true);
    const { data, error } = await supabase.functions.invoke("create-telegram-code");
    setCreatingCode(false);
    if (error || !data?.code) {
      telegramWindow?.close();
      toast({ title: "Fehler", description: "Code konnte nicht erstellt werden.", variant: "destructive" });
      return;
    }
    const nextCode = String(data.code).toUpperCase();
    setCode(nextCode);
    await supabase.from("clubs").update({ telegram_code: nextCode }).eq("id", club.id);
    const telegramUrl = `https://t.me/torflash_bot?start=${encodeURIComponent(nextCode)}`;
    if (telegramWindow) telegramWindow.location.href = telegramUrl;
    else window.location.href = telegramUrl;
  };

  const checkConnection = async (silent = false) => {
    if (!club || !code) return false;
    if (!silent) setChecking(true);
    const { data } = await supabase.functions.invoke("check-telegram-code", { body: { code } });
    if (data?.connected) {
      const { data: fresh } = await supabase.from("clubs").select("*").eq("id", club.id).maybeSingle();
      setClub((fresh as ClubData | null) ?? { ...club, telegram_chat_id: "connected" });
      toast({ title: "Verbunden", description: "Telegram ist jetzt mit deinem Verein verbunden." });
    } else if (!silent) {
      toast({ title: "Noch nicht verbunden", description: "Sende den Code an @torflash_bot und prüfe erneut.", variant: "destructive" });
    }
    if (!silent) setChecking(false);
    return !!data?.connected;
  };

  const handleCheckConnection = () => checkConnection(false);

  // Solange nicht verbunden: alle 5 Sekunden im Hintergrund prüfen.
  useEffect(() => {
    if (!club || club.telegram_chat_id || !code) return;
    const id = setInterval(() => { void checkConnection(true); }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club?.id, club?.telegram_chat_id, code]);



  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (user) {
      supabase.from("clubs").select("*").eq("user_id", user.id).maybeSingle().then(({ data }) => {
        const c = data as ClubData | null;
        setClub(c);
        if (c) {
          setSelectedDesign(normalizeDesign(c.selected_design));
          setPrimaryColor(c.primary_color || "#1B5EAB");
          // Nur Codes im gültigen Bot-Format anzeigen; alte Platzhalter ignorieren.
          if (c.telegram_code && TELEGRAM_CODE_RE.test(c.telegram_code.toUpperCase())) {
            setCode(c.telegram_code.toUpperCase());
          }


        }
        setLoading(false);
      });
      supabase
        .from("subscriptions")
        .select("id")
        .eq("user_id", user.id)
        .eq("environment", getStripeEnvironment())
        .limit(1)
        .then(({ data }) => setHasSubscription(!!data?.length));
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!club) return;

    let cancelled = false;
    const storedLogo = club.logo_url || "";
    if (storedLogo.startsWith("http") && storedLogo.length <= 500) {
      setResolvedLogoUrl(storedLogo);
    } else {
      setResolvedLogoUrl("");
    }

    // Ältere Konten können noch ein langes Proxy-Bild oder ein früheres
    // Testwappen gespeichert haben. Den Verein deshalb erneut exakt beim BFV
    // auflösen und die belastbare Vereinswappen-URL dauerhaft korrigieren.
    void fetch(`https://api.torflash.de/api/bfv-search?q=${encodeURIComponent(club.club_name)}`)
      .then((response) => response.json())
      .then((data) => {
        const matches = Array.isArray(data?.clubs) ? data.clubs : [];
        const exactClub = matches.find(
          (candidate: { clubName?: string }) => candidate.clubName?.trim().toLocaleLowerCase("de-DE") === club.club_name.trim().toLocaleLowerCase("de-DE"),
        );
        const freshLogo = getClubLogoUrl(exactClub);
        if (cancelled || !freshLogo) return;
        setResolvedLogoUrl(freshLogo);
        if (freshLogo !== storedLogo) {
          void supabase.from("clubs").update({ logo_url: freshLogo }).eq("id", club.id);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [club?.id, club?.club_name, club?.logo_url]);

  const hasChanges = useMemo(() => {
    if (!club) return false;
    return club.selected_design !== selectedDesign || club.primary_color !== primaryColor;
  }, [club, selectedDesign, primaryColor]);

  const handleSave = async () => {
    if (!club) return;
    if (!HEX_RE.test(primaryColor)) {
      toast({ title: "Ungültige Farbe", description: "Bitte gib einen gültigen Hex-Wert ein (z.B. #1B5EAB).", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("clubs")
      .update({ selected_design: selectedDesign, primary_color: primaryColor })
      .eq("id", club.id);
    setSaving(false);
    if (error) {
      toast({ title: "Fehler", description: "Änderungen konnten nicht gespeichert werden.", variant: "destructive" });
    } else {
      setClub({ ...club, selected_design: selectedDesign, primary_color: primaryColor });
      toast({ title: "Gespeichert", description: "Deine Einstellungen wurden aktualisiert." });
    }
  };

  const handleExport = () => {
    if (!club || !user) return;
    const exportData = {
      email: user.email,
      verein: club.club_name,
      mannschaft: club.team_name,
      telegram_status: club.telegram_chat_id ? "Verbunden" : "Nicht verbunden",
      telegram_code: club.telegram_code,
      registriert_am: new Date(club.created_at).toLocaleDateString("de-DE"),
      farben: { primär: club.primary_color },
      design: club.selected_design,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `torflash-daten-${club.club_name.toLowerCase().replace(/\s/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exportiert", description: "Deine Daten wurden heruntergeladen." });
  };

  const handleDelete = async () => {
    if (!confirm("Bist du sicher? Alle Daten werden unwiderruflich gelöscht.")) return;
    if (!user || !club) return;
    setDeleting(true);
    await supabase.from("clubs").delete().eq("id", club.id);
    await signOut();
    toast({ title: "Account gelöscht", description: "Dein Account und alle Daten wurden gelöscht." });
    navigate("/");
  };

  if (authLoading || loading) {
    return <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">Laden...</div>;
  }

  if (!club) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-muted-foreground">Noch kein Verein registriert.</p>
        <button onClick={() => navigate("/signup")} className="text-primary hover:underline">Verein registrieren</button>
      </div>
    );
  }

  const design = DESIGN_OPTIONS.find((d) => d.id === normalizeDesign(club.selected_design));
  const designThumbnail = (designId: string) => {
    const params = new URLSearchParams({
      club: club.club_name,
      pc: primaryColor,
      crestAway: "",
      opp: "FC Musterstadt",
      scale: "0.5",
      // Verhindert, dass eine früher mit demselben Vereinsnamen erzeugte
      // Vorschau samt falschem Testwappen aus dem Renderer-Cache kommt.
      crestVersion: resolvedLogoUrl || club.club_name,
    });
    if (resolvedLogoUrl) params.set("crest", resolvedLogoUrl);
    return `${RENDER_BASE}/preview/${designId}/matchday-post?${params.toString()}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="container max-w-5xl py-8 space-y-6">
        {/* Header card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center gap-4">
            {resolvedLogoUrl ? (
              <img src={resolvedLogoUrl} alt={club.club_name} className="h-14 w-14 rounded-full object-contain bg-white border border-border" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full font-display text-lg font-bold text-white" style={{ backgroundColor: club.primary_color }}>
                {club.club_name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-foreground">{club.club_name}</h1>
              <p className="text-sm text-muted-foreground">{club.team_name || "1. Mannschaft"} · {club.liga}</p>
            </div>
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">Design: {design?.name ?? club.selected_design}</span>
          </div>
        </div>

        {/* Design / Farbe */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <div>
            <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Design & Farbe</h2>
            <p className="mt-1 text-sm text-muted-foreground">Hier siehst du dein aktuell gewähltes Design.</p>
          </div>

          <div>
            <Label className="font-display uppercase tracking-wider text-xs">Gewähltes Design</Label>
            <div className="mt-3 max-w-sm overflow-hidden rounded-lg border border-primary bg-primary/5 p-1.5 ring-2 ring-primary/20">
              <div className="w-full overflow-hidden rounded-md bg-muted">
                <ScaledLivePreview src={designThumbnail(selectedDesign)} title={`Design ${selectedDesign}`} height={1350} />
              </div>
              <div className="flex items-center justify-between px-2 py-2">
                <span className="text-sm font-bold text-foreground">Design {selectedDesign}</span>
                <Check className="h-4 w-4 text-primary" />
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="mt-3 gap-2"
              aria-expanded={designPickerOpen}
              onClick={() => setDesignPickerOpen((open) => !open)}
            >
              Anderes Design wählen
              <ChevronDown className={`h-4 w-4 transition-transform ${designPickerOpen ? "rotate-180" : ""}`} />
            </Button>

            {designPickerOpen ? (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {DESIGN_OPTIONS.filter((d) => d.id !== selectedDesign).map((d) => {
                return (
                  <Button
                    key={d.id}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedDesign(d.id);
                      setDesignPickerOpen(false);
                    }}
                    className="relative h-auto min-w-0 flex-col gap-0 overflow-hidden rounded-lg border-border p-1.5"
                  >
                    <div className="w-full overflow-hidden rounded-md bg-muted">
                      <ScaledLivePreview src={designThumbnail(d.id)} title={d.name} height={1350} />
                    </div>
                    <span className="py-2 text-sm font-bold text-foreground">{d.name}</span>
                  </Button>
                );
              })}
              </div>
            ) : null}
          </div>

          <div>
            <Label>Primärfarbe</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={HEX_RE.test(primaryColor) ? primaryColor : "#1B5EAB"}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-10 cursor-pointer rounded border border-input"
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#1B5EAB"
                maxLength={7}
                className="font-mono text-sm max-w-[140px]"
              />
              <span className="text-xs text-muted-foreground">Wird in deinen Stories verwendet.</span>
            </div>
          </div>

          <Button variant="hero" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? "Wird gespeichert..." : "Änderungen speichern"}
          </Button>
        </div>

        {/* Telegram */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Telegram</h2>

          {club.telegram_chat_id ? (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-lg">✅</span>
              <span className="text-sm text-foreground">Telegram ist verbunden. Du erhältst deine Stories automatisch.</span>
            </div>
          ) : Boolean(club.instagram_enabled) ? (
            <div className="mt-4 flex items-start gap-2">
              <Instagram className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">
                Grafiken werden automatisch auf eurem Instagram gepostet – Telegram wird dafür nicht benötigt.
              </p>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Verbinde deinen Verein mit Telegram, um fertige Stories direkt aufs Handy zu bekommen.
              </p>

              <Button variant="hero" onClick={handleConnectTelegram} disabled={creatingCode} className="gap-2">
                <Send className="h-4 w-4" />
                {creatingCode ? "Telegram wird geöffnet..." : "Telegram jetzt verbinden"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Telegram öffnet sich. Tippe dort auf „Start“ – die Verbindung wird anschließend automatisch erkannt.
              </p>
              {code ? (
                <Button variant="outline" size="sm" onClick={handleCheckConnection} disabled={checking}>
                  {checking ? "Wird geprüft..." : "Verbindung prüfen"}
                </Button>
              ) : null}
            </div>
          )}
        </div>


        {/* Sponsoren & Fotos */}
        {user && (
          <ClubMediaSection
            clubId={club.id}
            clubName={club.club_name}
            userId={user.id}
            sponsorUrls={club.sponsor_urls ?? []}
            sponsorsEnabled={club.sponsors_enabled ?? true}
            photos={{
              photo_matchday: club.photo_matchday ?? null,
              photo_goal: club.photo_goal ?? null,
              photo_final: club.photo_final ?? null,
            }}
          />
        )}

        {/* Mannschaften & Instagram */}
        {user && (
          <TeamsSection
            clubId={club.id}
            clubName={club.club_name}
            userId={user.id}
            youthPack={Boolean(club.youth_pack)}
            instagramEnabled={Boolean(club.instagram_enabled)}
            instagramConnected={Boolean(club.instagram_connected)}
            selectedTier={club.selected_tier}
          />
        )}

        {/* Abo & Rechnungen */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Abo & Rechnungen</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasSubscription
              ? "Zahlungsmethode ändern, Rechnungen ansehen oder Abo jederzeit kündigen."
              : "Du hast noch kein aktives Abo. Starte deine 14 Tage kostenlos."}
          </p>
          {hasSubscription ? (
            <div className="mt-4">
              {PORTAL_URL ? (
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                    <CreditCard className="h-3.5 w-3.5" /> Abo verwalten
                  </a>
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" disabled className="gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" /> Abo verwalten
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">Abo-Verwaltung wird gerade eingerichtet</p>
                </>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Du bekommst per E-Mail einen Login-Link. Dort siehst du deine Rechnungen, kannst die Zahlungsart ändern oder kündigen.
              </p>
            </div>
          ) : (
            <Button variant="hero" size="sm" onClick={() => navigate("/checkout")} className="mt-4 gap-1.5">
              <CreditCard className="h-3.5 w-3.5" /> Abo starten
            </Button>
          )}
        </div>

        {/* Account data section */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Account-Daten</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">E-Mail</p>
              <p className="text-sm font-medium text-foreground">{user?.email}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Verein</p>
              <p className="text-sm font-medium text-foreground">{club.club_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Mannschaft</p>
              <p className="text-sm font-medium text-foreground">{club.team_name || "1. Mannschaft"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Telegram-Status</p>
              <p className="text-sm font-medium text-foreground">{club.telegram_chat_id ? "✅ Verbunden" : "⏳ Nicht verbunden"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Registriert am</p>
              <p className="text-sm font-medium text-foreground">{new Date(club.created_at).toLocaleDateString("de-DE")}</p>
            </div>
          </div>

          <div className="mt-5 flex items-start gap-2 rounded-lg bg-muted/50 p-3">
            <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              Deine Daten werden in einer sicheren Datenbank in der EU gespeichert. Wir geben deine Daten nicht an Dritte weiter.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5">
              <Download className="h-3.5 w-3.5" /> Daten exportieren
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Wird gelöscht..." : "Account löschen"}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
